import { useState, useEffect } from 'react';
import { Users, Shield, Gift, Eye, EyeOff, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { axiosInstance } from '../../lib/api-client';
import { logger } from '../../utils/logger';

interface MarketingUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'marketing' | 'marketing_manager';
  canCreatePromotionalOrders: boolean;
  showPromotionsTab: boolean;
}

export function MarketingUserPermissionsManager() {
  const [users, setUsers] = useState<MarketingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null); // Format: "userId-permissionType"

  useEffect(() => {
    fetchMarketingUsers();
  }, []);

  const fetchMarketingUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/marketing-users/promotional-permissions');

      if (response.data.success) {
        setUsers(response.data.data.users);
      }
    } catch (error: any) {
      logger.error('Error fetching marketing users:', error);
      toast.error('Failed to fetch marketing users');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = async (
    userId: string,
    permissionType: 'canCreatePromotionalOrders' | 'showPromotionsTab',
    currentValue: boolean
  ) => {
    try {
      setUpdating(`${userId}-${permissionType}`);
      
      const response = await axiosInstance.patch(
        `/admin/marketing-users/${userId}/promotional-permissions`,
        {
          [permissionType]: !currentValue
        }
      );

      if (response.data.success) {
        // Update local state
        setUsers(users.map(user => 
          user._id === userId 
            ? { ...user, [permissionType]: !currentValue }
            : user
        ));
        
        toast.success(`Permission updated successfully`);
      }
    } catch (error: any) {
      logger.error('Error updating permission:', error);
      toast.error('Failed to update permission');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-rose-500" />
          <p className="text-gray-500">Loading marketing users...</p>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center p-12 bg-gray-50 rounded-lg">
        <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500">No marketing users found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-rose-500" />
        <div>
          <h3 className="text-xl font-bold text-gray-900">Marketing User Permissions</h3>
          <p className="text-sm text-gray-600">Control promotional order access for individual marketing users</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-900">Total Users</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{users.length}</p>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-900">Can Create Offers</span>
          </div>
          <p className="text-2xl font-bold text-green-900">
            {users.filter(u => u.canCreatePromotionalOrders).length}
          </p>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-purple-900">Tab Visible</span>
          </div>
          <p className="text-2xl font-bold text-purple-900">
            {users.filter(u => u.showPromotionsTab).length}
          </p>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Show Promotions Tab
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Create Offers
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge 
                      className={
                        user.role === 'marketing_manager'
                          ? 'bg-purple-100 text-purple-800 hover:bg-purple-100'
                          : 'bg-blue-100 text-blue-800 hover:bg-blue-100'
                      }
                    >
                      {user.role === 'marketing_manager' ? 'Manager' : 'Marketing'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Button
                      size="sm"
                      variant={user.showPromotionsTab ? 'default' : 'outline'}
                      onClick={() => togglePermission(user._id, 'showPromotionsTab', user.showPromotionsTab)}
                      disabled={updating === `${user._id}-showPromotionsTab`}
                      className={
                        user.showPromotionsTab
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }
                    >
                      {updating === `${user._id}-showPromotionsTab` ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : user.showPromotionsTab ? (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Visible
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4 mr-2" />
                          Hidden
                        </>
                      )}
                    </Button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Button
                      size="sm"
                      variant={user.canCreatePromotionalOrders ? 'default' : 'outline'}
                      onClick={() => togglePermission(user._id, 'canCreatePromotionalOrders', user.canCreatePromotionalOrders)}
                      disabled={updating === `${user._id}-canCreatePromotionalOrders`}
                      className={
                        user.canCreatePromotionalOrders
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }
                    >
                      {updating === `${user._id}-canCreatePromotionalOrders` ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : user.canCreatePromotionalOrders ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Enabled
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2" />
                          Disabled
                        </>
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-2">Permission Guidelines:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Show Promotions Tab:</strong> Controls whether the user can see the Promotions tab in their panel</li>
              <li><strong>Create Offers:</strong> Allows the user to create new promotional orders for customers</li>
              <li><strong>Applies to All Marketing Roles:</strong> These permissions control access for both Marketing Users and Marketing Managers</li>
              <li><strong>Admin Exception:</strong> Admin users always have full access regardless of these settings</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}